import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { campaignDefinition } from '../../../../../../entity-definitions/campaign.definition';
import { PermissionFacadeService } from '../../../../../../services/permission-facade.service';
import { InfoStore } from '../../../../../../stores/info.store';
import { MeasureTypeStore } from '../../../../../../stores/measure-type.store';
import { Campaign } from '../../../../../../types/campaign.interface';
import { ActionButtonsConfigSet, ColumnConfig, EntityDefinition } from '../../../../../../types/entity-definition.interface';
import { Info } from '../../../../../../types/info.interface';
import { Measure } from '../../../../../../types/measure.interface';
import { MeasureType } from '../../../../../../types/measure-type.interface';
import { CesCheckableMenuItem } from '../../../../../ui/ces-checkable-menu/ces-checkable-menu.directive';
import { CampaignDataService } from './campaign-data.service';
import { CampaignNavigationService } from './campaign-navigation.service';
import { CompletedCampaignsGridStore } from './completed-campaigns-grid.store';
import { CompletionStatus } from '../../../../../../types/completion-status.enum';

const COMPLETION_STATUS_KEYS = {
  [CompletionStatus.DARK_GREEN]: 'entities.campaign.completionStatus.complete',
  [CompletionStatus.LIGHT_GREEN]: 'entities.campaign.completionStatus.planned',
  [CompletionStatus.ORANGE]: 'entities.campaign.completionStatus.incomplete',
  [CompletionStatus.WHITE]: 'entities.campaign.completionStatus.notPlanned',
} as const satisfies Partial<Record<CompletionStatus, string>>;

@Injectable({
  providedIn: 'root',
})
export class CampaignGridConfigService {
  private readonly permissionFacade = inject(PermissionFacadeService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  private readonly measureTypeStore = inject(MeasureTypeStore);
  private readonly infoStore = inject(InfoStore);
  private readonly nav = inject(CampaignNavigationService);
  private readonly data = inject(CampaignDataService);
  private readonly gridStore = inject(CompletedCampaignsGridStore);

  readonly showInfoColumn = computed(() => this.gridStore.showInfoColumn());
  readonly showCommentColumn = computed(() => this.gridStore.showCommentColumn());

  readonly canManageCampaigns = this.permissionFacade.hasWriteAccess('monitoring/campaigns');

  readonly availableMeasureTypes = computed(() => {
    const assetId = this.nav.currentAssetId();
    if (!assetId) return [];

    const availableMeasures = this.data.currentAvailableMeasures();
    const availableMeasureTypeIds = new Set(availableMeasures.map((measure: Measure) => measure.measureTypeId));
    return this.measureTypeStore.entities().filter((measureType: MeasureType) => availableMeasureTypeIds.has(measureType.id));
  });

  readonly selectedMeasureTypeColumns = computed(() => {
    const deselected = new Set(this.gridStore.deselectedMeasureTypes());
    return this.availableMeasureTypes()
      .filter((measureType: MeasureType) => !deselected.has(measureType.id))
      .map((measureType: MeasureType) => measureType.id);
  });

  readonly hasInfos = computed(() => {
    const assetId = this.nav.currentAssetId();
    if (!assetId) return false;
    return this.infoStore.entities().some((info: Info) => info.assetId === assetId);
  });

  readonly columnSelectorMenuItems = computed((): CesCheckableMenuItem[] => {
    const selectedIds = this.selectedMeasureTypeColumns();
    return this.availableMeasureTypes().map((measureType: MeasureType) => ({
      label: measureType.name,
      checkable: true,
      checked: selectedIds.includes(measureType.id),
      command: () => this.toggleMeasureTypeColumn(measureType.id),
    }));
  });

  readonly combinedColumnMenuItems = computed((): CesCheckableMenuItem[] => {
    this.translations();
    const commentLabel = this.translate.translate('common.labels.comment');
    const infoLabel = this.translate.translate('common.labels.auscultationInfo');

    const items: CesCheckableMenuItem[] = [
      {
        label: commentLabel,
        checkable: true,
        checked: this.showCommentColumn(),
        command: () => this.toggleCommentColumn(),
      },
      ...this.columnSelectorMenuItems(),
    ];

    if (this.hasInfos()) {
      items.push({
        label: infoLabel,
        checkable: true,
        checked: this.showInfoColumn(),
        command: () => this.toggleInfoColumn(),
      });
    }

    return items.sort((a, b) => {
      if (a.label === commentLabel) return -1;
      if (b.label === commentLabel) return 1;
      return (a.label ?? '').localeCompare(b.label ?? '');
    });
  });

  readonly completionFilterOptions = computed(() => {
    this.translations();
    return [
      { label: this.translate.translate(COMPLETION_STATUS_KEYS[CompletionStatus.DARK_GREEN]), value: CompletionStatus.DARK_GREEN },
      { label: this.translate.translate(COMPLETION_STATUS_KEYS[CompletionStatus.LIGHT_GREEN]), value: CompletionStatus.LIGHT_GREEN },
      { label: this.translate.translate(COMPLETION_STATUS_KEYS[CompletionStatus.ORANGE]), value: CompletionStatus.ORANGE },
      { label: this.translate.translate(COMPLETION_STATUS_KEYS[CompletionStatus.WHITE]), value: CompletionStatus.WHITE },
    ];
  });

  readonly dateColumn = computed((): ColumnConfig => {
    this.translations();
    return {
      field: 'timestamp',
      header: this.translate.translate('common.labels.date'),
      sortable: true,
      filterable: true,
      filterType: 'datetime',
      template: 'datetime',
      frozen: true,
      class: 'date-column',
    };
  });

  readonly commentTableColumn = computed((): ColumnConfig[] => {
    this.translations();
    if (!this.showCommentColumn()) return [];
    return [
      {
        field: 'comment',
        header: this.translate.translate('common.labels.comment'),
        sortable: true,
        filterable: true,
        class: 'comment-column',
        contentClass: 'text-wrap-clamp',
        showTooltip: true,
      },
    ];
  });

  readonly measureTableColumns = computed((): ColumnConfig[] => {
    const selectedMeasureTypes = this.availableMeasureTypes().filter((mt: MeasureType) =>
      this.selectedMeasureTypeColumns().includes(mt.id),
    );
    return selectedMeasureTypes.map((measureType: MeasureType) => ({
      field: `measureType_${measureType.id}`,
      header: measureType.name,
      sortable: false,
      filterable: 'select',
      class: 'text-center measure-type-column',
      template: 'campaignMeasureTypeCompletion',
      options: this.completionFilterOptions(),
    }));
  });

  readonly infoTableColumns = computed((): ColumnConfig[] => {
    if (!this.showInfoColumn() || !this.hasInfos()) return [];
    return [
      {
        field: 'infoCompletion',
        header: this.translate.translate('common.labels.auscultationInfo'),
        sortable: false,
        filterable: 'select',
        class: 'text-center measure-type-column',
        template: 'campaignMeasureTypeCompletion',
        options: this.completionFilterOptions(),
      },
    ];
  });

  readonly allTableColumns = computed((): ColumnConfig[] => {
    return [this.dateColumn(), ...this.commentTableColumn(), ...this.measureTableColumns(), ...this.infoTableColumns()];
  });

  readonly tableActionButtons = computed<ActionButtonsConfigSet>(() => {
    return {
      parent: {
        add: false,
        edit: false,
      },
    };
  });

  readonly tableDefinition = computed((): EntityDefinition<Campaign> => {
    const assetId = this.nav.currentAssetId();
    return {
      ...campaignDefinition,
      list: {
        columns: this.allTableColumns(),
      },
      actionButtons: this.tableActionButtons(),
      enableFiltering: true,
      rowLinkHref: campaign => (assetId ? ['/assets', assetId, 'monitoring', 'campaigns', campaign.id] : null),
    };
  });

  constructor() {
    // The store enriches rows with puces from the effective selection but cannot derive it
    // itself: available measure types live outside the store, so mirror the selection in.
    effect(() => this.gridStore.setSelectedMeasureTypes(this.selectedMeasureTypeColumns()));
  }

  isFiltering(): boolean {
    return this.selectedMeasureTypeColumns().length > 0 || this.showInfoColumn();
  }

  toggleMeasureTypeColumn(measureTypeId: number): void {
    const deselected = this.gridStore.deselectedMeasureTypes();
    const newDeselection = deselected.includes(measureTypeId)
      ? deselected.filter(id => id !== measureTypeId)
      : [...deselected, measureTypeId];
    this.gridStore.setDeselectedMeasureTypes(newDeselection);
  }

  checkAllMeasureTypeColumns(): void {
    const availableIds = new Set(this.availableMeasureTypes().map((mt: MeasureType) => mt.id));
    this.gridStore.setDeselectedMeasureTypes(this.gridStore.deselectedMeasureTypes().filter(id => !availableIds.has(id)));
  }

  uncheckAllMeasureTypeColumns(): void {
    const deselected = new Set(this.gridStore.deselectedMeasureTypes());
    this.availableMeasureTypes().forEach((mt: MeasureType) => deselected.add(mt.id));
    this.gridStore.setDeselectedMeasureTypes([...deselected]);
  }

  toggleInfoColumn(): void {
    this.gridStore.setShowInfoColumn(!this.showInfoColumn());
  }

  toggleCommentColumn(): void {
    this.gridStore.setShowCommentColumn(!this.showCommentColumn());
  }

  checkAllColumns(): void {
    this.checkAllMeasureTypeColumns();
    if (!this.showCommentColumn()) {
      this.gridStore.setShowCommentColumn(true);
    }
    if (this.hasInfos() && !this.showInfoColumn()) {
      this.gridStore.setShowInfoColumn(true);
    }
  }

  uncheckAllColumns(): void {
    this.uncheckAllMeasureTypeColumns();
    if (this.showCommentColumn()) {
      this.gridStore.setShowCommentColumn(false);
    }
    if (this.showInfoColumn()) {
      this.gridStore.setShowInfoColumn(false);
    }
  }
}
